import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS } from './useLanguage';
import { SITE_URL } from '@/lib/site';

const LANG_PREFIX: Record<string, string> = {
  en: '',
  pl: '/pl',
  es: '/es',
  fr: '/fr',
};

function buildUrl(lang: string, slug: string): string {
  const prefix = LANG_PREFIX[lang] ?? '';
  const path = slug ? `${prefix}/${slug}` : prefix || '/';
  return `${SITE_URL}${path}`;
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let link = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    if (hreflang) link.setAttribute('hreflang', hreflang);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Dynamically manages canonical and hreflang tags for a given page slug
 * (empty string = homepage). Ensures each language variant points to its
 * own localized URL.
 */
export function useSeo(slug: string) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language && SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'en';

    // Canonical for the current language + page
    upsertLink('canonical', buildUrl(lang, slug));

    // hreflang for every supported language
    SUPPORTED_LANGS.forEach((l) => {
      upsertLink('alternate', buildUrl(l, slug), l);
    });

    // x-default points to the English version
    upsertLink('alternate', buildUrl('en', slug), 'x-default');
  }, [i18n.language, slug]);
}